import type { SplitMethod } from 'types/model/fraction';

// Debe coincidir con Fractions::SplitFractionService::MAX_PARTS.
export const MAX_PARTS = 200;

export const round2 = (value: number) => Math.round(value * 100) / 100;

export interface SplitPreview {
  weights: number[];
  // PLA-860 v3 (D2): "por cantidad" con peso de saco, pero con mas partes que sacos completos.
  equalPartsFallback: boolean;
  // PLA-860 v3 (D2): kg del saco incompleto que suma la ultima parte en el reparto por sacos.
  partialBagKg?: number;
}

// Se redondea a 6 decimales solo para absorber el error de coma flotante antes del ceil/floor.
const absorbFloat = (value: number) => Math.round(value * 1e6) / 1e6;

const equalWeights = (weight: number, parts: number): number[] => {
  const rounded = round2(weight / parts);

  return [...new Array(parts - 1).fill(rounded), round2(weight - rounded * (parts - 1))];
};

// PLA-860 v3 (W1, D2): sacos completos repartidos lo mas parejo posible (las primeras llevan un
// saco mas) y la ultima suma el saco incompleto.
const balancedBagWeights = (
  weight: number,
  parts: number,
  bagWeightKg: number,
  wholeBags: number
): number[] => {
  const bags = Math.floor(wholeBags / parts);
  const extra = wholeBags % parts;
  const weights = Array.from({ length: parts }, (_, index) =>
    round2((index < extra ? bags + 1 : bags) * bagWeightKg)
  );

  weights[parts - 1] = round2(weights[parts - 1] + weight - wholeBags * bagWeightKg);

  return weights;
};

// PLA-860 v2 (D1): misma formula que el servidor, solo para la vista previa.
export const previewSplit = (
  weight: number,
  method: SplitMethod,
  amount: number,
  bagWeightKg?: number | null
): SplitPreview => {
  const empty = { weights: [], equalPartsFallback: false };

  if (!(weight > 0) || !(amount > 0)) {
    return empty;
  }

  if (method === 'by_bags') {
    if (!bagWeightKg) {
      return empty;
    }

    const groupWeight = round2(bagWeightKg * amount);
    const count = Math.ceil(absorbFloat(weight / groupWeight));

    if (count > MAX_PARTS) {
      return { ...empty, weights: new Array(MAX_PARTS + 1).fill(groupWeight) };
    }

    return {
      ...empty,
      weights: [
        ...new Array(count - 1).fill(groupWeight),
        round2(weight - groupWeight * (count - 1)),
      ],
    };
  }

  if (amount < 2) {
    return empty;
  }

  if (amount > MAX_PARTS) {
    return { ...empty, weights: new Array(MAX_PARTS + 1).fill(0) };
  }

  if (!bagWeightKg) {
    return { ...empty, weights: equalWeights(weight, amount) };
  }

  const wholeBags = Math.floor(absorbFloat(weight / bagWeightKg));

  if (wholeBags < amount) {
    return { weights: equalWeights(weight, amount), equalPartsFallback: true };
  }

  return {
    ...empty,
    weights: balancedBagWeights(weight, amount, bagWeightKg, wholeBags),
    partialBagKg: round2(weight - wholeBags * bagWeightKg),
  };
};

export const previewWeights = (
  weight: number,
  method: SplitMethod,
  amount: number,
  bagWeightKg?: number | null
): number[] => previewSplit(weight, method, amount, bagWeightKg).weights;
